import styled from '@emotion/styled'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Input, Tooltip, Upload, message } from 'antd'
import { isString } from 'markdown-it/lib/common/utils'
import { remove } from 'lodash-es'
import { useClickAway } from 'ahooks'
import { Editor } from '../../editor'
import { IconFont } from '@/components'
import { MAX_SPLIT_SIZE } from '@/features/datastore/constant'
import { checkFileSize } from '@/features/datastore/utils'

const Wrapper = styled.div<{ disabled?: boolean; isEdited?: boolean }>`
  &:hover {
    .info-box,
    .delete-btn {
      opacity: 1;
      width: auto;
    }
  }

  .paragraph-editor,
  .paragraph-editor > *:not(.paragraph-split-mark) {
    // ${props => props.disabled && 'color: rgba(141, 141, 153, 0.6);'}
  }

  .paragraph-editor {
    padding: 0px;
    outline: 0px;
    font-size: 14px;
    * {
      line-height: 22px;
    }
  }

  .paragraph-editor[contenteditable='true']:empty::before {
    content: '请输入答案内容';
    font-size: 14px;
    line-height: 16px;
    color: rgba(141, 141, 153, 0.6);
  }

  .edited-mark:before {
    content: '';
    width: 24px;
    height: 24px;
    background: #38a6ff;
    opacity: 0.4;
    position: absolute;
    top: -12px;
    left: -12px;
    pointer-events: none;
    transform: rotate(45deg);
  }

  img {
    min-width: 24px;
    min-height: 24px;
    max-width: 400px;
    max-height: 200px;
    display: block;
  }

  video {
    width: 100%;
    height: 100%;
    max-width: 400px;
    max-height: 200px;
  }
`

const StyledInput = styled(Input)`
  .ant-input-show-count-suffix {
    font-size: 10px !important;
  }
`

function AEditor(props: any) {
  const {
    value = '',
    title,
    onTitleChange,
    file_id,
    ossClient,
    readOnly,
    onRemove,
    showRemoveTag,
    onChange,
  } = props
  const [focus, setFocus] = useState(false)
  const editorInterface = useRef<Editor>()
  const editorRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [count, setCount] = useState(value?.length)

  const [isEdit, setEdit] = useState(false)
  const [name, setName] = useState(title)

  const fnRef = useRef<(val?: string) => void>()
  fnRef.current = onChange
  const nameRef = useRef(null)
  const inputRef = useRef<any>(null)

  const onImageUpload = async (file: File) => {
    if (ossClient) {
      const filePath = `i/datastore_qa/${file_id}/${+new Date()}/${file.name}`

      ossClient.useBucket(import.meta.env.VITE_AI_DATASTORE_RESOURCE_BUCKET)
      const res = await ossClient.put(filePath, file)

      return res.url
    }
    throw new Error('oss client is not init')
  }

  const loadEditor = () => {
    editorInterface.current = new Editor({
      dom: editorRef.current!,
      content: isString(value) ? value : value.join('\n'),
      onImageUpload,
      enableSplitMark: false,
      editEnable: !readOnly,
      renderHtml: true,
    })
    editorInterface.current.init()
    editorInterface.current.registryEvents('onFocus', () => setFocus(true))
    editorInterface.current.registryEvents('onBlur', () => setFocus(false))
    editorInterface.current.registryEvents('onChange', () => {
      fnRef?.current?.(editorInterface?.current?.content)
      setCount(editorInterface.current?.textCount ?? 0)
    })
  }

  const beforeUpload = (file: File): any => {
    if (!checkFileSize(file.size, 20)) {
      message.warning('图片大小不能超过20MB, 请修改后重新上传')
      return Upload.LIST_IGNORE
    }
    return true
  }

  const uploadProps = {
    customRequest: (ad: any) => {
      editorInterface.current?.insertImageIntoContent(
        editorInterface.current.editor,
        [ad.file],
      )
    },
    beforeUpload,
    accept: 'image/*',
    maxCount: 1,
    itemRender: () => null,
    showUploadList: false,
  }

  useEffect(() => {
    loadEditor()
    return () => {
      editorInterface.current?.destroy()
    }
  }, [readOnly, ossClient])

  const onSaveTitle = () => {
    setEdit(false)
    onTitleChange(name)
  }

  useClickAway(() => {
    if (isEdit) {
      setEdit(false)
      onSaveTitle()
    }
  }, nameRef)

  const moreThanMaxSize = count > MAX_SPLIT_SIZE

  return (
    <Wrapper>
      <div
        className={classNames(
          'bg-white rounded-8px b-1 b-[rgba(225,225,229,0.8)] px-12px py-8px',
          {
            'cursor-pointer': isEdit,
          },
        )}
      >
        <div
          ref={wrapperRef}
          onClick={() => {
            onSaveTitle()
          }}
          className='flex items-center flex w-100%'
        >
          {isEdit ? (
            <div className='flex flex-1' ref={nameRef}>
              <StyledInput
                size='small'
                value={name}
                maxLength={10}
                ref={inputRef}
                onClick={e => {
                  e.stopPropagation()
                }}
                count={{
                  show: true,
                  max: 10,
                }}
                className={classNames('max-w-320px', {
                  'cursor-not-allowed': readOnly,
                })}
                onPressEnter={onSaveTitle}
                onChange={e => setName?.(e.target.value)}
              />
            </div>
          ) : (
            <div className='flex text-14px line-height-24px c-#17171D'>
              {title}
            </div>
          )}
          {!isEdit && !readOnly && (
            <div className='flex-1 ml-4px'>
              <IconFont
                onClick={() => {
                  setTimeout(() => {
                    setEdit(true)
                  })
                }}
                name='bianji'
                style={{
                  color: 'rgba(98, 105, 153, 0.6)',
                  borderRadius: 4,
                  boxSizing: 'content-box',
                }}
                className='text-16px cursor-pointer p-4px hover:bg-#62696D hover:bg-op-8'
              />
            </div>
          )}
          {!readOnly && showRemoveTag && (
            <Tooltip title='删除答案'>
              <span
                className='self-end w-24px h-24px rounded-4px hover:bg-[rgba(255,82,25,0.08)] hover:bg-op-12 c-bg_3 text-16px c-op-60 hover:c-error  cursor-pointer flex-center'
                onClick={(e: any) => {
                  e.stopPropagation()
                  onRemove()
                }}
              >
                <IconFont name='shanshu' />
              </span>
            </Tooltip>
          )}
        </div>
        <div className='relative'>
          <div
            className={classNames(
              'mt-12 px-12px py-8px pb-32px editor-wrap  rounded-8px  text-14px line-height-22px b-1 b-transparent',
              {
                'bg-[rgba(98,105,153,0.06)]': !readOnly,
                '!pl-0px': readOnly,
                '!b-primary': focus && !moreThanMaxSize,
                '!b-error': moreThanMaxSize,
                'hover:!b-primary': !readOnly && !moreThanMaxSize,
                'hover:!b-error': !readOnly && moreThanMaxSize,
              },
            )}
            ref={editorRef}
          />
          {!readOnly && (
            <div>
              <Upload
                {...uploadProps}
                className='absolute bottom-[8px] text-20px w-24px h-24px left-8px z-1 cursor-pointer hover:bg-#62696D hover:bg-op-8 rounded-4px flex items-center justify-center'
              >
                <Tooltip title='上传图片'>
                  <IconFont name='shangzhuantupian' className='c-#B9B9CD' />
                </Tooltip>
              </Upload>
            </div>
          )}
          <div
            className={classNames(
              'absolute right-12px bottom-[12px] flex items-center info-box',
              {
                '!op-0': readOnly,
              },
            )}
          >
            <span
              className={classNames('c-font_1 text-12px font-400', {
                'c-error': false,
              })}
            >
              <span
                className={classNames({
                  'c-##8D8D99': focus,
                  '!c-error': moreThanMaxSize,
                })}
              >
                {count}
              </span>
              {!readOnly && <span> / {MAX_SPLIT_SIZE}</span>}
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

export default (props: any) => {
  const { value, onChange, readOnly = false } = props

  const onValueChange = (idx: number, val: string) => {
    const newValue = [...value]
    const item = newValue[idx]
    item.value = val
    newValue[idx] = item
    onChange?.(newValue)
  }

  const onTitleChange = (idx: number, title: string) => {
    const newValue = [...value]
    const item = newValue[idx]
    item.title = title
    newValue[idx] = item
    onChange?.(newValue)
  }

  const onRemove = (idx: number) => {
    const newVal = [...value]
    remove(newVal, (_i, index) => index === idx)
    onChange?.(newVal)
  }

  const showRemoveTag = useMemo(() => {
    return value.length > 1
  }, [value])

  return value?.map(
    (item: { title: string; value: any; id: number }, index: number) => {
      return (
        <div key={item.id} className='mt-12px'>
          <AEditor
            {...props}
            readOnly={readOnly}
            value={item.value}
            title={item.title}
            showRemoveTag={showRemoveTag}
            onChange={(value: string) => {
              onValueChange(index, value)
            }}
            onTitleChange={(values: string) => {
              onTitleChange(index, values)
            }}
            onRemove={() => {
              onRemove(index)
            }}
          />
        </div>
      )
    },
  )
}
